import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Columns3, Check } from 'lucide-react';
import { Button } from './button';
import { cn } from '@/lib/utils';
import type { ColumnDef } from '@/hooks/useColumnPreferences';

interface Props {
  columns: readonly ColumnDef[];
  visible: Set<string>;
  onToggle: (key: string) => void;
}

export function ColumnPicker({ columns, visible, onToggle }: Props): JSX.Element {
  const toggleable = columns.filter((c) => !c.locked);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5 text-muted-foreground">
          <Columns3 className="h-3.5 w-3.5" />
          Columns
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className={cn(
            'z-50 min-w-[11rem] overflow-hidden rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-overlay',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
            'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
          )}
        >
          {toggleable.map((col) => {
            const checked = visible.has(col.key);
            return (
              <DropdownMenu.CheckboxItem
                key={col.key}
                checked={checked}
                onCheckedChange={() => onToggle(col.key)}
                onSelect={(e) => e.preventDefault()}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors',
                  'focus:bg-accent focus:text-accent-foreground',
                )}
              >
                <span
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-input',
                    checked && 'border-primary bg-primary text-primary-foreground',
                  )}
                >
                  {checked && <Check className="h-3 w-3" />}
                </span>
                {col.label}
              </DropdownMenu.CheckboxItem>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
